import { resolvePersonality } from "../bot-seed.js";
import { BOT_DIFFICULTIES, BOT_PERSONALITIES } from "../content/setup.js";
import type { RoomOrderAck, RoomRole } from "../index.js";
import type { OnlineRoomSession, OnlineRoomSnapshot } from "../online-room-session.js";

export interface OnlineViewState {
  ready: boolean;
  status: string;
  chatDraft: string;
  roomSave: string;
}

export function renderOnline(
  root: HTMLElement,
  snapshot: OnlineRoomSnapshot,
  session: OnlineRoomSession,
  submitTarget: (targetSeatId?: string) => Promise<RoomOrderAck>,
  roomId: string,
  role: RoomRole,
  sessionId: string,
  relayUrl: string | undefined,
  viewState: OnlineViewState,
): void {
  root.replaceChildren();
  const invite = new URL(location.href);
  invite.search = new URLSearchParams({ room: roomId, role: "controller" }).toString();
  if (relayUrl) invite.searchParams.set("relay", relayUrl);
  const roomLine = element("p", `Room ${roomId} · ${role}`);
  roomLine.dataset.onlineRoom = roomId;
  const network = element(
    "p",
    `${snapshot.directConnections}/${snapshot.peerCount} direct WebRTC peers connected`,
  );
  network.dataset.webrtc = String(snapshot.directConnections);
  root.append(element("h1", "Kilnfront online room"), roomLine, network);
  if (role === "host") renderInvite(root, invite.href);
  renderRecovery(root, snapshot);
  if (snapshot.phase === "error") root.append(notice(snapshot.error ?? "Online room failed"));
  else if ((snapshot.phase === "playing" || snapshot.phase === "recovering") && snapshot.state)
    renderMatch(root, snapshot, session, submitTarget, sessionId, viewState);
  else renderLobby(root, snapshot, session, viewState);
  renderChat(root, snapshot, session, viewState);
  if (viewState.status) {
    const line = element("p", viewState.status);
    line.dataset.orderStatus = "true";
    root.append(line);
  }
  const exit = document.createElement("a");
  exit.href = location.pathname;
  exit.textContent = "Leave online room";
  root.append(exit);
}

export function autosaveSingleHuman(
  session: OnlineRoomSession,
  snapshot: OnlineRoomSnapshot,
  roomId: string,
): void {
  const humans = snapshot.state?.lifecycle.controllers.filter(
    (entry) => entry.mode !== "computer" && entry.sessionId !== null,
  );
  if (humans?.length !== 1) return;
  try {
    const save = session.availableSave();
    if (save) localStorage.setItem(`kilnfront-online-autosave:${roomId}`, save);
  } catch (error) {
    console.warn("Kilnfront single-human autosave failed", error);
  }
}

function renderRecovery(root: HTMLElement, snapshot: OnlineRoomSnapshot): void {
  if (!snapshot.recovery) return;
  const recovery = element(
    "p",
    `Authority epoch ${snapshot.recovery.authorityEpoch} · ${snapshot.recovery.message}`,
  );
  recovery.dataset.recovery = snapshot.recovery.frozen ? "frozen" : "active";
  if (snapshot.recovery.frozen) recovery.setAttribute("role", "alert");
  root.append(recovery);
}

function renderInvite(root: HTMLElement, href: string): void {
  const label = element("label", "Controller invite");
  const output = document.createElement("input");
  output.readOnly = true;
  output.value = href;
  output.setAttribute("aria-label", "Controller invite");
  label.append(output);
  root.append(label);
}

function renderLobby(
  root: HTMLElement,
  snapshot: OnlineRoomSnapshot,
  session: OnlineRoomSession,
  viewState: OnlineViewState,
): void {
  root.append(element("h2", snapshot.phase === "connecting" ? "Finding host…" : "Room lobby"));
  if (snapshot.config)
    root.append(
      element(
        "p",
        `${snapshot.config.mode ?? "every-town-for-itself"} · ${snapshot.config.mapId ?? "ring"} · ${snapshot.config.seats.length} towns`,
      ),
    );
  const list = document.createElement("ul");
  list.dataset.roster = "true";
  for (const entry of snapshot.roster)
    list.append(
      element(
        "li",
        `${entry.sessionId} — ${entry.role}${entry.seatId ? ` / ${entry.seatId}` : ""} — ${entry.ready ? "ready" : "not ready"}`,
      ),
    );
  root.append(list);
  if (snapshot.roster.filter((entry) => entry.role !== "spectator").length === 2) {
    const warning = element(
      "p",
      "Two-peer host loss cannot form a recovery majority. The survivor pauses and may later load a retained save as a new room.",
    );
    warning.dataset.recoveryWarning = "two-peer";
    root.append(warning);
  }
  if (snapshot.role !== "spectator" && snapshot.phase === "lobby")
    root.append(
      button(
        viewState.ready ? "Not ready" : "Ready",
        async () => {
          viewState.ready = !viewState.ready;
          await session.setReady(viewState.ready);
        },
        "ready-room",
      ),
    );
  if (snapshot.role === "host" && snapshot.phase === "lobby")
    root.append(button("Start room", () => session.start(), "start-room"));
}

function renderMatch(
  root: HTMLElement,
  snapshot: OnlineRoomSnapshot,
  session: OnlineRoomSession,
  submitTarget: (targetSeatId?: string) => Promise<RoomOrderAck>,
  sessionId: string,
  viewState: OnlineViewState,
): void {
  const observation = snapshot.state?.observation;
  if (!observation) return;
  const heading = element("h2", `${observation.self.name} — live shift`);
  const state = element(
    "p",
    `Time ${observation.gameTimeMs}ms · revision ${observation.revision} · metal ${observation.self.metal}`,
  );
  state.dataset.gameTime = String(observation.gameTimeMs);
  state.dataset.revision = String(observation.revision);
  state.dataset.seat = snapshot.seatId ?? "spectator";
  root.append(heading, state);
  renderControllers(root, snapshot);
  renderPause(root, snapshot, session, sessionId);
  renderRecoveryActions(root, snapshot, session, sessionId, viewState);
  if (snapshot.role !== "spectator")
    for (const opponent of observation.seats.filter((seat) => seat.id !== snapshot.seatId))
      root.append(
        button(`Target ${opponent.name}`, () => submitTarget(opponent.id), `target-${opponent.id}`),
      );
}

function renderRecoveryActions(
  root: HTMLElement,
  snapshot: OnlineRoomSnapshot,
  session: OnlineRoomSession,
  sessionId: string,
  viewState: OnlineViewState,
): void {
  if (snapshot.role !== "spectator")
    root.append(
      button(
        "Export acknowledged room save",
        async () => {
          viewState.roomSave = session.exportSave();
          viewState.status = "Latest majority-acknowledged room save exported.";
        },
        "export-room-save",
      ),
    );
  if (viewState.roomSave) {
    const output = document.createElement("textarea");
    output.readOnly = true;
    output.value = viewState.roomSave;
    output.setAttribute("aria-label", "Exported room save");
    root.append(output);
  }
  if (snapshot.recovery?.hostMemberId !== sessionId) return;
  for (const memberId of snapshot.recovery.voters.filter((entry) => entry !== sessionId))
    root.append(
      button(
        `Hand off host to ${memberId}`,
        () => session.handoff(memberId),
        `handoff-${memberId}`,
      ),
    );
}

function renderControllers(root: HTMLElement, snapshot: OnlineRoomSnapshot): void {
  const lifecycle = snapshot.state?.lifecycle;
  if (!lifecycle) return;
  const list = document.createElement("ul");
  list.dataset.controllers = "true";
  for (const controller of lifecycle.controllers) {
    const seat = snapshot.state?.observation.seats.find((entry) => entry.id === controller.seatId);
    const configured = snapshot.config?.seats.find((entry) => entry.id === controller.seatId)?.bot;
    const personality = resolvePersonality(
      configured?.personality ?? "seeded-mix",
      snapshot.config?.rootSeed ?? "kilnfront-online-bots",
      controller.seatId,
    );
    const personalityName =
      BOT_PERSONALITIES.find((entry) => entry.id === personality)?.name ?? personality;
    const difficultyName =
      BOT_DIFFICULTIES.find((entry) => entry.id === (configured?.difficulty ?? "foreman"))?.name ??
      "Foreman";
    const label =
      controller.mode === "grace" && controller.disconnectedAtGameMs !== null
        ? `Disconnected — grace since ${controller.disconnectedAtGameMs}ms`
        : controller.mode === "autopilot"
          ? "Autopilot — Foreman Defender"
          : controller.mode === "computer"
            ? `Computer-controlled — ${difficultyName} ${personalityName}`
            : "Human-controlled";
    list.append(element("li", `${seat?.name ?? controller.seatId}: ${label}`));
  }
  root.append(element("h3", "Controllers"), list);
}

function renderPause(
  root: HTMLElement,
  snapshot: OnlineRoomSnapshot,
  session: OnlineRoomSession,
  sessionId: string,
): void {
  if (snapshot.role === "spectator" || !snapshot.state) return;
  const lifecycle = snapshot.state.lifecycle;
  const proposal = lifecycle.pauseProposal;
  const status = element("p", lifecycle.paused ? "Room paused." : "Room running.");
  status.dataset.pauseState = lifecycle.paused ? "paused" : "running";
  root.append(status);
  if (!proposal)
    root.append(
      button(
        lifecycle.paused ? "Request resume" : "Request pause",
        () => session.requestPause(!lifecycle.paused),
        "request-pause",
      ),
    );
  else if (!proposal.approvals.includes(sessionId))
    root.append(
      element(
        "p",
        `${proposal.paused ? "Pause" : "Resume"} requested by ${proposal.requestedBy}. Your approval is required.`,
      ),
      button("Approve", () => session.approvePause(), "approve-pause"),
    );
  else
    root.append(
      element("p", `Waiting for unanimous ${proposal.paused ? "pause" : "resume"} approval.`),
    );
}

function renderChat(
  root: HTMLElement,
  snapshot: OnlineRoomSnapshot,
  session: OnlineRoomSession,
  viewState: OnlineViewState,
): void {
  root.append(element("h2", "Room chat"));
  const log = document.createElement("ol");
  log.dataset.chat = "true";
  for (const message of snapshot.chat)
    log.append(element("li", `${message.senderSessionId}: ${message.text}`));
  const input = document.createElement("input");
  input.maxLength = 256;
  input.value = viewState.chatDraft;
  input.setAttribute("aria-label", "Chat message");
  input.addEventListener("input", () => {
    viewState.chatDraft = input.value;
  });
  root.append(
    log,
    input,
    button(
      "Send chat",
      async () => {
        await session.sendChat(viewState.chatDraft);
        viewState.chatDraft = "";
        const current = root.querySelector<HTMLInputElement>('[aria-label="Chat message"]');
        if (current) current.value = "";
      },
      "send-chat",
    ),
  );
}

function button(label: string, action: () => Promise<unknown>, id: string): HTMLButtonElement {
  const result = document.createElement("button");
  result.type = "button";
  result.textContent = label;
  result.dataset.action = id;
  result.addEventListener("click", () => {
    result.disabled = true;
    result.removeAttribute("aria-invalid");
    result.parentElement?.querySelector(`[data-action-error="${id}"]`)?.remove();
    Promise.resolve(action())
      .catch((error) => {
        result.setAttribute("aria-invalid", "true");
        const message = error instanceof Error ? error.message : "Online action failed";
        result.title = message;
        const warning = element("span", message);
        warning.dataset.actionError = id;
        warning.setAttribute("role", "alert");
        result.after(warning);
      })
      .finally(() => {
        if (result.isConnected) result.disabled = false;
      });
  });
  return result;
}

function element<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  text: string,
): HTMLElementTagNameMap[K] {
  const result = document.createElement(tag);
  result.textContent = text;
  return result;
}

function notice(text: string): HTMLElement {
  const result = element("p", text);
  result.setAttribute("role", "alert");
  return result;
}
